import { Box } from '@mui/material';
import { useCompanyRelativePath, useGetOGPUserInfo } from 'ogp/hooks';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { Header, Page } from 'redesign';
import { routes } from 'Routes';
import { invariant } from 'shared/utils/utils';
import { useAlert } from 'shared/components/alerts';
import { RenderUnsettledUI } from 'shared/components';
import { useSiteDetection } from 'shared/hooks';
import { useGetFlexPoolsForFlexWorker } from 'hq/components/views/flexWorkers/flex-worker-detail/tabs/flex-pools/use-get-flex-pools-for-flex-worker';
import { useTranslation } from 'react-i18next';
import { ToolboxForm } from 'shared/components/common/toolbox-form';
import { FlexPool } from 'hq/services/flex-pool-service.types';
import { DateTime } from 'luxon';
import {
  EmployeeResponse,
  EmployeeUpdatePayload,
} from 'api/services/workforce/workforce-service.types';
import { useMemo } from 'react';
import { EmployeeForm } from '../employee-form/employee-form';
import { useUpdateEmployee } from '../../hooks/use-update-employee';
import { useGetEmployeeDetail } from '../../hooks/use-get-employee-detail';
import { resolveContractMinutes } from '../../../../../../../shared/utils/resolve-contract-minutes';

export type EmployeeFormData = {
  email: string;
  firstName: string;
  infix?: string;
  lastName: string;
  employeeFlexPools: FlexPool[];
  contractStartDate?: string;
  contractEndDate: string | undefined;
  contractHours: number | undefined;
};

export const EmployeeUpdate = () => {
  const { id: workerId } = useParams<'id'>();
  invariant(workerId, 'Flex worker ID must be passed as a route param');

  const { site } = useSiteDetection();
  const employeeQuery = useGetEmployeeDetail(workerId ?? '');
  const flexPoolsQuery = useGetFlexPoolsForFlexWorker(site, workerId);

  if (employeeQuery.status !== 'success' || flexPoolsQuery.status !== 'success') {
    return <RenderUnsettledUI data={flexPoolsQuery} />;
  }

  return (
    <EmployeeUpdateImpl
      workerId={workerId}
      flexPoolsData={flexPoolsQuery.data}
      employeeData={employeeQuery.data}
    />
  );
};

export const EmployeeUpdateImpl = ({
  workerId,
  flexPoolsData,
  employeeData,
}: {
  workerId: string;
  flexPoolsData: FlexPool[];
  employeeData: EmployeeResponse;
}) => {
  const userInfo = useGetOGPUserInfo();
  const navigate = useNavigate();
  const generatePath = useCompanyRelativePath();
  const { alertSuccess, alertError } = useAlert();
  const { t } = useTranslation(['web-common', 'ogp-screen-specific']);

  const flexPoolsForCompany = useMemo(
    () =>
      flexPoolsData.filter(
        (flexPool) => flexPool.companyId === userInfo.data?.userInfo.company.slug
      ),
    [flexPoolsData, userInfo.data?.userInfo.company.slug]
  );

  const formMethods = useForm<EmployeeFormData>({
    defaultValues: {
      email: employeeData.email,
      firstName: employeeData.firstName ?? '',
      infix: employeeData.infix ?? '',
      lastName: employeeData.lastName ?? '',
      employeeFlexPools: flexPoolsForCompany,
      contractStartDate: employeeData.contractStartDate
        ? DateTime.fromISO(employeeData.contractStartDate).toISODate()
        : undefined,

      contractEndDate: employeeData.contractEndDate
        ? DateTime.fromISO(employeeData.contractEndDate).toISODate()
        : undefined,
      contractHours: employeeData.contractMinutes ? employeeData.contractMinutes / 60 : undefined,
    },
  });

  const employeeMutation = useUpdateEmployee(workerId);

  const handleSubmit = () => {
    formMethods.handleSubmit((data) => {
      const mappedData = mapData(data);
      employeeMutation.mutate(mappedData, {
        onSuccess: () => {
          navigate(generatePath(routes.EmployeeDetail, { id: workerId }));
          alertSuccess(
            t('employee_edit_screen.update_employee_success', { ns: 'ogp-screen-specific' })
          );
        },
        onError: (err) => alertError(err),
      });
    })();
  };

  const mapData = (data: EmployeeFormData): EmployeeUpdatePayload => {
    if (employeeData.isLentEmployee) {
      return {
        employeeFlexPools: data.employeeFlexPools,
      };
    }

    return {
      employeeFlexPools: data.employeeFlexPools,
      contractStartDate: data.contractStartDate,
      contractEndDate: data.contractEndDate,
      contractMinutes: resolveContractMinutes(data.contractHours),
    };
  };

  return (
    <Page
      header={<Header titleText={t('employee_edit_screen.title', { ns: 'ogp-screen-specific' })} />}
      toolbox={
        <ToolboxForm
          onSubmit={handleSubmit}
          mutationStatus={employeeMutation.status}
          mode="update"
          onCancel={() => navigate(generatePath(routes.Workforce))}
        />
      }
    >
      <Box width="100%">
        <FormProvider {...formMethods}>
          <EmployeeForm formState="update" isLentEmployee={employeeData.isLentEmployee} />
        </FormProvider>
      </Box>
    </Page>
  );
};
